// Mirrors the ranking algorithm in pages/WorstCase.js (rankedRows useMemo, minus the
// tie-detection/rationale text which is UI-only). Kept here so other pages — e.g. Sampling
// Plans defaulting "Previous API" to the worst-case API for a train — can reuse the same
// scoring formula instead of re-deriving it. If the scoring logic in WorstCase.js changes,
// mirror the change here too.

export const DEFAULT_WEIGHTS = {
  solubility: 10,
  pde: 6,
  therapeutic: 4,
  cleanability: 1
};

export const DEFAULT_SOLUBILITY = {
  'very soluble': 1,
  'freely soluble': 1,
  soluble: 2,
  'sparingly soluble': 2,
  'slightly soluble': 3,
  'very slightly soluble': 3,
  'practically insoluble': 3,
  insoluble: 3
};

export const SOLUBILITY_SUB_ORDER = {
  'very soluble': 1,
  'freely soluble': 2,
  soluble: 3,
  'sparingly soluble': 4,
  'slightly soluble': 5,
  'very slightly soluble': 6,
  'practically insoluble': 7,
  insoluble: 8
};

export const DEFAULT_CLEANABILITY = { easy: 1, medium: 2, difficult: 3 };

export const DEFAULT_PDE_RATINGS = { gt500: 1, from100to500: 2, from10to99: 3, from1to9: 4, lt1: 5 };

export const DEFAULT_DOSE_RATINGS = { gt1000: 1, from100to1000: 2, from10to99: 3, from1to9: 4, lt1: 5 };

export const DEFAULT_PRIORITY_ORDER = ['solubility', 'cleanability', 'pde', 'dose'];

export const WORST_CASE_SETTINGS_KEYS = {
  weights: 'worst_case_weights',
  solubility: 'worst_case_solubility_ratings',
  cleanability: 'worst_case_cleanability_ratings',
  pde: 'worst_case_pde_ratings',
  dose: 'worst_case_therapeutic_dose_ratings',
  scoringMethod: 'worst_case_scoring_method',
  priorityOrder: 'worst_case_priority_order',
  manualOverrides: 'worst_case_manual_overrides',
};

export const parseJsonSetting = (rawValue, fallback) => {
  if (!rawValue) return fallback;
  try {
    const parsed = JSON.parse(rawValue);
    return parsed && typeof parsed === 'object' ? (Array.isArray(fallback) ? (Array.isArray(parsed) ? parsed : fallback) : { ...fallback, ...parsed }) : fallback;
  } catch {
    return fallback;
  }
};

export const parseJsonObject = (rawValue) => {
  if (!rawValue) return {};
  try {
    const parsed = JSON.parse(rawValue);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch { return {}; }
};

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeUnit = (unit) => String(unit || '').trim().toLowerCase();

const convertToMcg = (value, unit) => {
  const numeric = toNumber(value, 0);
  const normalizedUnit = normalizeUnit(unit);
  if (!normalizedUnit || normalizedUnit === 'mcg' || normalizedUnit === 'ug') return numeric;
  if (normalizedUnit === 'mg') return numeric * 1000;
  if (normalizedUnit === 'g') return numeric * 1000000;
  return numeric;
};

const convertToMg = (value, unit) => {
  const numeric = toNumber(value, 0);
  const normalizedUnit = normalizeUnit(unit);
  if (!normalizedUnit || normalizedUnit === 'mg') return numeric;
  if (normalizedUnit === 'mcg' || normalizedUnit === 'ug') return numeric / 1000;
  if (normalizedUnit === 'g') return numeric * 1000;
  return numeric;
};

const getCriterionRating = (row, key) => {
  switch (key) {
    case 'solubility': return row.solubility_rating;
    case 'cleanability': return row.cleanability_rating;
    case 'pde': return row.pde_rating;
    case 'dose': return row.therapeutic_dose_rating;
    default: return 0;
  }
};

const tiebreakActual = (a, b) => {
  if (a.pde_mcg !== b.pde_mcg) return a.pde_mcg - b.pde_mcg;
  const aSolSub = SOLUBILITY_SUB_ORDER[String(a.solubility_in_water || '').toLowerCase()] || 4;
  const bSolSub = SOLUBILITY_SUB_ORDER[String(b.solubility_in_water || '').toLowerCase()] || 4;
  if (bSolSub !== aSolSub) return bSolSub - aSolSub;
  if (a.therapeutic_dose_mg !== b.therapeutic_dose_mg) return a.therapeutic_dose_mg - b.therapeutic_dose_mg;
  if (b.product_batch_size !== a.product_batch_size) return b.product_batch_size - a.product_batch_size;
  return String(a.api_name || '').localeCompare(String(b.api_name || ''));
};

// Ranks (train_id, api) pairs by cleaning-validation risk score, same formula as
// pages/WorstCase.js. Returns rows sorted highest-risk first, each tagged with
// wcr_rating (1 = worst case) scoped to `trainFilter` ('All' or a train name).
export const computeWorstCaseRanking = ({
  trains = [], apis = [], products = [],
  weights = DEFAULT_WEIGHTS,
  solubilityRatings = DEFAULT_SOLUBILITY,
  cleanabilityRatings = DEFAULT_CLEANABILITY,
  pdeRatings = DEFAULT_PDE_RATINGS,
  doseRatings = DEFAULT_DOSE_RATINGS,
  scoringMethod = 'weighted',
  priorityOrder = DEFAULT_PRIORITY_ORDER,
  manualOverrides = {},
  trainFilter = 'All'
}) => {
  const trainNameById = {};
  trains.forEach((train) => {
    trainNameById[String(train.train_id)] = train.train_name || train.train_code || `Train ${train.train_id}`;
  });

  const apiMasterByKey = {};
  apis.forEach((api) => {
    const idKey = Number(api?.api_id);
    if (Number.isFinite(idKey)) apiMasterByKey[`id:${idKey}`] = api;
    const codeKey = String(api?.api_code || '').trim().toLowerCase();
    if (codeKey) apiMasterByKey[`code:${codeKey}`] = api;
    const nameKey = String(api?.api_name || '').trim().toLowerCase();
    if (nameKey) apiMasterByKey[`name:${nameKey}`] = api;
  });

  const productApiRows = [];
  products.forEach((product) => {
    const productApis = Array.isArray(product?.apis)
      ? product.apis
      : (() => {
          try {
            const parsed = JSON.parse(product?.apis || '[]');
            return Array.isArray(parsed) ? parsed : [];
          } catch {
            return [];
          }
        })();

    const linkedTrainIds = [...new Set([
      ...(Array.isArray(product?.linked_trains) ? product.linked_trains.map((link) => String(link?.train_id || '')).filter(Boolean) : []),
      ...(product?.equipment_train_id ? [String(product.equipment_train_id)] : [])
    ])];
    const productTrains = linkedTrainIds.length ? linkedTrainIds : ['N/A'];

    productApis.forEach((apiRef) => {
      const apiId = Number(apiRef?.api_id);
      const apiCode = String(apiRef?.api_code || '').trim();
      const apiName = String(apiRef?.api_name || '').trim();
      const mergedApi =
        (Number.isFinite(apiId) && apiMasterByKey[`id:${apiId}`]) ||
        (apiCode && apiMasterByKey[`code:${apiCode.toLowerCase()}`]) ||
        (apiName && apiMasterByKey[`name:${apiName.toLowerCase()}`]) ||
        {};

      productTrains.forEach((trainId) => {
        const linkedTrain = Array.isArray(product?.linked_trains)
          ? product.linked_trains.find((link) => String(link?.train_id) === trainId)
          : null;
        productApiRows.push({
          ...mergedApi,
          ...apiRef,
          api_id: Number.isFinite(apiId) ? apiId : (mergedApi.api_id || `${apiCode || apiName}-${product.product_id}`),
          api_name: apiName || mergedApi.api_name || '-',
          product_id: product.product_id,
          product_name: product.product_name || '-',
          product_batch_size: toNumber(product.batch_size, 0),
          product_batch_size_unit: product.batch_size_unit || '',
          product_status: product.status,
          train_id: trainId,
          train_name: trainNameById[trainId] || linkedTrain?.train_name || linkedTrain?.train_code || 'No Train'
        });
      });
    });
  });

  const sourceRows = productApiRows.length > 0
    ? productApiRows
    : apis.map((api) => ({
        ...api,
        api_name: api.api_name || '-',
        product_name: '-',
        product_batch_size: 0,
        product_batch_size_unit: '',
        train_id: 'N/A',
        train_name: 'No Train'
      }));

  const groupedLargestRows = {};
  sourceRows.forEach((row) => {
    const groupKey = `${row.train_id}__${row.api_id || row.api_name}`;
    const current = groupedLargestRows[groupKey];
    if (!current || toNumber(row.product_batch_size, 0) > toNumber(current.product_batch_size, 0)) {
      groupedLargestRows[groupKey] = row;
    }
  });

  const baseRows = Object.values(groupedLargestRows).map((row) => {
    const solubilityText = String(row.solubility_classification || '').trim();
    const cleanabilityText = String(row.cleanability_rating || '').trim();
    const solubilityKey = solubilityText.toLowerCase();
    const cleanabilityKey = cleanabilityText.toLowerCase();
    const solubilityRating = toNumber(solubilityRatings[solubilityKey], 3);
    const cleanabilityRating = toNumber(cleanabilityRatings[cleanabilityKey], 3);
    const pdeMg = convertToMg(row.pde_value, row.pde_unit || 'mg');
    const pdeMcg = convertToMcg(row.pde_value, row.pde_unit || 'mg');

    let pdeRating = toNumber(pdeRatings.lt1, 5);
    if (pdeMcg > 500) pdeRating = toNumber(pdeRatings.gt500, 1);
    else if (pdeMcg >= 100) pdeRating = toNumber(pdeRatings.from100to500, 2);
    else if (pdeMcg >= 10) pdeRating = toNumber(pdeRatings.from10to99, 3);
    else if (pdeMcg >= 1) pdeRating = toNumber(pdeRatings.from1to9, 4);

    const therapeuticDoseMg = convertToMg(row.therapeutic_dose, row.therapeutic_dose_unit || 'mg');
    let therapeuticRating = toNumber(doseRatings.lt1, 5);
    if (therapeuticDoseMg > 1000) therapeuticRating = toNumber(doseRatings.gt1000, 1);
    else if (therapeuticDoseMg >= 100) therapeuticRating = toNumber(doseRatings.from100to1000, 2);
    else if (therapeuticDoseMg >= 10) therapeuticRating = toNumber(doseRatings.from10to99, 3);
    else if (therapeuticDoseMg >= 1) therapeuticRating = toNumber(doseRatings.from1to9, 4);

    const riskRating =
      (solubilityRating * toNumber(weights.solubility, 10)) +
      (pdeRating * toNumber(weights.pde, 6)) +
      (therapeuticRating * toNumber(weights.therapeutic, 4)) +
      (cleanabilityRating * toNumber(weights.cleanability, 1));

    return {
      ...row,
      solubility_in_water: solubilityText || '-',
      solubility_rating: solubilityRating,
      cleanability: cleanabilityText || '-',
      cleanability_rating: cleanabilityRating,
      pde_mg: pdeMg,
      pde_mcg: pdeMcg,
      pde_rating: pdeRating,
      therapeutic_dose_mg: therapeuticDoseMg,
      therapeutic_dose_rating: therapeuticRating,
      risk_rating: Math.round(riskRating),
      // Approved only when both the API and the product behind this row are
      // Approved — a Draft/Pending row must never outrank (and silently
      // replace) an already-Approved worst case just by scoring higher.
      is_provisional: String(row.status || '').trim() !== 'Approved' || String(row.product_status || '').trim() !== 'Approved'
    };
  });

  const expandedRows = baseRows.filter((row) => trainFilter === 'All' || row.train_name === trainFilter);

  const order = (priorityOrder && priorityOrder.length === 4) ? priorityOrder : DEFAULT_PRIORITY_ORDER;

  const taggedRows = expandedRows.map((row) => {
    const overrideKey = `${row.train_id}__${row.api_id}`;
    return { ...row, manual_wc: manualOverrides[overrideKey] || null };
  });

  const sortFn = scoringMethod === 'priority'
    ? (a, b) => {
        if (!!a.manual_wc !== !!b.manual_wc) return a.manual_wc ? -1 : 1;
        if (!!a.is_provisional !== !!b.is_provisional) return a.is_provisional ? 1 : -1;
        for (const key of order) {
          const diff = getCriterionRating(b, key) - getCriterionRating(a, key);
          if (diff !== 0) return diff;
        }
        return tiebreakActual(a, b);
      }
    : (a, b) => {
        if (!!a.manual_wc !== !!b.manual_wc) return a.manual_wc ? -1 : 1;
        if (!!a.is_provisional !== !!b.is_provisional) return a.is_provisional ? 1 : -1;
        if (b.risk_rating !== a.risk_rating) return b.risk_rating - a.risk_rating;
        for (const key of order) {
          const diff = getCriterionRating(b, key) - getCriterionRating(a, key);
          if (diff !== 0) return diff;
        }
        return tiebreakActual(a, b);
      };

  taggedRows.sort(sortFn);

  const rowsForRanking = trainFilter === 'All'
    ? (() => {
        const seen = new Set();
        return taggedRows.filter((row) => {
          const key = String(row.api_id ?? row.api_name ?? '').toLowerCase();
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      })()
    : taggedRows;

  let globalRank = 0;
  return rowsForRanking.map((row) => {
    globalRank += 1;
    return { ...row, wcr_rating: globalRank };
  });
};
